import { css } from "@emotion/css";
import { Button } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import { getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled } from "@octopusdeploy/octopus-server-client";
import React, { useEffect, useMemo, useState } from "react";
import type { ArgoCDInstanceConfiguration } from "~/areas/infrastructure/components/ArgoCDInstances/ArgoCDInstanceConfiguration";
import { ConfigurationMessages } from "~/areas/infrastructure/components/ArgoCDInstances/DialogViews/ConfigurationMessages";
import { LocalDevServerUrlHelper } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/LocalDevServerUrlHelper";
import { useEphemeralEnvironmentsEnabled } from "~/areas/projects/components/EphemeralEnvironments/useEphemeralEnvironmentsEnabled";
import { EnvironmentV2MultiSelect } from "~/components/MultiSelect/EnvironmentV2MultiSelect";
import { required, Text } from "~/components/form/index";
type ConfigurationPageProps = {
    model: ArgoCDInstanceConfiguration;
    setModel: (configuration: Partial<ArgoCDInstanceConfiguration>) => void;
    getFieldError: (fieldName: keyof ArgoCDInstanceConfiguration) => string | undefined;
    isSelfHosted: boolean;
};
const styles = {
    showAdvancedButtonStyle: css({
        alignSelf: "flex-start",
        margin: `${space[8]} 0`,
    }),
};
export const ConfigurationView = (props: ConfigurationPageProps) => {
    const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
    const ephemeralEnvironmentsAreEnabled = useEphemeralEnvironmentsEnabled();
    const { result: environments } = useInlineStatusQuery((repo) => getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled(ephemeralEnvironmentsAreEnabled, repo), [ephemeralEnvironmentsAreEnabled], "List all environments");
    const environmentIdToSlugLookup = useMemo(() => {
        const map: Record<string, string | undefined> = {};
        for (const env of environments || []) {
            map[env.Id] = env.Slug;
        }
        return map;
    }, [environments]);
    useEffect(() => {
        //if there is an error with the server url or server grpc url, and they aren't visible, make them visible
        if (!showAdvanced && (props.getFieldError("octopusServerUrl") || props.getFieldError("octopusGrpcUrl"))) {
            setShowAdvanced(true);
        }
    }, [props, props.getFieldError, showAdvanced]);
    return (<>
            <Text value={props.model.name} helperText={ConfigurationMessages.Name.HelperText} onChange={(name) => props.setModel({ name })} label={"Name"} error={props.getFieldError("name")} validate={required(ConfigurationMessages.Name.Validation.NotProvided)} autoFocus={true}/>
            <EnvironmentV2MultiSelect environments={environments || []} hideFloatingLabel={false} openOnFocus={false} error={props.getFieldError("environmentIds")} onChange={(envIds) => props.setModel({ environmentIds: envIds, environmentSlugs: envIds.map((id) => environmentIdToSlugLookup[id] || id) })} helperText={props.getFieldError("environmentIds") ? "" : ConfigurationMessages.Environments.HelperText} value={props.model.environmentIds} label="Environments"/>
            <Text value={props.model.argoCDUrl} helperText={ConfigurationMessages.ArgoCDUri.HelperText} onChange={(url) => props.setModel({ argoCDUrl: url })} label={"Argo CD API server URL"} error={props.getFieldError("argoCDUrl")} validate={required(ConfigurationMessages.ArgoCDUri.Validation.NotProvided)}/>
            <Text value={props.model.argoCDAuthenticationToken} helperText={ConfigurationMessages.ArgoCDAuthToken.HelperText} onChange={(token) => props.setModel({ argoCDAuthenticationToken: token })} label={"Authentication Token"} error={props.getFieldError("argoCDAuthenticationToken")} validate={required(ConfigurationMessages.ArgoCDAuthToken.Validation.NotProvided)}/>
            {
        //self-hosted customers can change the urls
        props.isSelfHosted && (<>
                        <LocalDevServerUrlHelper onServerAddressesChanged={(serverUri, _x, grpcUri) => {
                if (!showAdvanced) {
                    setShowAdvanced(true);
                }
                props.setModel({
                    octopusServerUrl: serverUri,
                    octopusGrpcUrl: grpcUri,
                });
            }}/>
                        <div className={styles.showAdvancedButtonStyle}>
                            <Button label={showAdvanced ? "Hide optional settings" : "Show optional settings"} importance="tertiary" onClick={(e) => {
                e.preventDefault();
                setShowAdvanced((prev) => !prev);
            }}/>
                        </div>
                        {showAdvanced && (<>
                                <Text value={props.model.octopusServerUrl} onChange={(url) => props.setModel({ octopusServerUrl: url })} error={props.getFieldError("octopusServerUrl")} label="Octopus Deploy Server URL" helperText={ConfigurationMessages.ServerUri.HelperText} validate={required(ConfigurationMessages.ServerUri.Validation.NotProvided)}/>
                                <Text value={props.model.octopusGrpcUrl} onChange={(url) => props.setModel({ octopusGrpcUrl: url })} error={props.getFieldError("octopusGrpcUrl")} label="Octopus Deploy Server gRPC URL" helperText={ConfigurationMessages.ServerGrpcUri.HelperText} validate={required(ConfigurationMessages.ServerGrpcUri.Validation.NotProvided)}/>
                            </>)}
                    </>)}
        </>);
};
